// src/components/ExtensionRequestPanel.js
//
// T5 item 6 (2026-10-15) - rulings 133 and 134 on the claim page: the claimant records his own written request to
// extend a time limit. The LATE-1 caution (lateOne.js, Bob's approved words) is shown EVERY time the right is offered,
// never alone and never as a block (ruling 133). Past the ownership threshold (the representative carries the claim)
// the form is not offered and the reason is given. Everyone who reads the claim sees the requests and whether each
// was on time on the member's own act (ruling 134). src\data\extensionRequests.js decides; this file draws and calls
// the extensionRequest function.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { MEANS, MEANS_LABELS, TEXT_MAX, mayRequest } from '../data/extensionRequests';
import { factsCaller } from './FactsActsPanel';
import { LATE1_TITLE, LATE1_INTRO, LATE1_CONDITIONS, LATE1_CARRIER_LINE, LATE1_RECOMMENDATION_TITLE, LATE1_RECOMMENDATION } from '../data/lateOne';

const C = { line: '#2E4A6B', dim: '#8AA4BF', text: '#DCE7F2', ok: '#5FB878', bad: '#E06C6C', accent: '#E8A020' };

export default function ExtensionRequestPanel({ claim, claimId, user }) {
  const [rows, setRows] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [f, setF] = useState({ means: 'email' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!claimId) return undefined;
    const q = query(collection(db, 'claims', claimId, 'extensionRequests'), orderBy('at', 'desc'));
    return onSnapshot(q, snap => { setRows(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoadErr(''); },
      e => setLoadErr((e && (e.code || e.message)) || 'refused'));
  }, [claimId]);

  if (!claim || !claim.status || claim.status === 'draft') return null;
  const caller = factsCaller(user);
  const isClaimant = mayRequest({ ...claim, carriedBy: 'claimant' }, caller).may;   // is he the claimant at all
  const m = mayRequest(claim, caller);
  if (!isClaimant && rows.length === 0 && !loadErr) return null;   // officers see the section only when there is a record
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const send = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = (await httpsCallable(getFunctions(), 'extensionRequest')({ claimId, ...f })).data;
      setF({ means: 'email' });
      setMsg({ ok: r.timely !== false, text: 'Recorded. ' + r.line + ((r.unaddressed || []).length ? ' Not told: ' + r.unaddressed.join('; ') + '.' : ' Your Local Chairperson and General Chairperson are told.') });
    } catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not recorded.' }); }
    setBusy(false);
  };

  return (
    <div style={{ border: '1px solid ' + C.line, padding: '10px 14px', margin: '12px 0' }}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>Requests to extend a time limit</div>
      {isClaimant ? (
        <div style={{ margin: '6px 0 10px' }}>
          {/* Ruling 133: the caution travels with the right - every time it is offered. */}
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{LATE1_TITLE}</div>
          <div style={{ fontSize: 12, color: C.dim }}>{LATE1_INTRO}</div>
          <ul style={{ margin: '4px 0 6px', paddingLeft: 18, fontSize: 12, color: C.dim }}>{LATE1_CONDITIONS.map(c => <li key={c}>{c}</li>)}</ul>
          <div style={{ fontSize: 12, color: C.dim }}>{LATE1_CARRIER_LINE}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginTop: 6 }}>{LATE1_RECOMMENDATION_TITLE}</div>
          {LATE1_RECOMMENDATION.map(t => <div key={t} style={{ fontSize: 12, color: C.dim }}>{t}</div>)}
          {!m.may ? (
            <div style={{ fontSize: 12, color: C.accent, marginTop: 8 }}>{m.because}</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
              <input aria-label="Which time limit (e.g. filing, appeal)" className="form-input" placeholder="Which time limit (e.g. filing, appeal)" value={f.timeLimit || ''} onChange={e => set('timeLimit', e.target.value)} />
              <label style={{ fontSize: 11, color: C.dim }}>Its last day <input aria-label="Expires on" className="form-input" type="date" value={f.expiresOn || ''} onChange={e => set('expiresOn', e.target.value)} /></label>
              <label style={{ fontSize: 11, color: C.dim }}>Date you sent the request <input aria-label="Its last day" className="form-input" type="date" value={f.sentOn || ''} onChange={e => set('sentOn', e.target.value)} /></label>
              <input aria-label="Date you sent the request" className="form-input" placeholder="Carrier officer you sent it to" value={f.sentTo || ''} onChange={e => set('sentTo', e.target.value)} />
              <select aria-label="How the extension was agreed" className="form-select" value={f.means} onChange={e => set('means', e.target.value)}>{MEANS.map(x => <option key={x} value={x}>{MEANS_LABELS[x]}</option>)}</select>
              <input aria-label="Your proof: timestamp, receipt or tracking no." className="form-input" placeholder="Your proof: timestamp, receipt or tracking no." value={f.proof || ''} onChange={e => set('proof', e.target.value)} style={{ flexBasis: '100%' }} />
              <textarea aria-label="What you asked for (optional)" className="form-input" rows={2} maxLength={TEXT_MAX} placeholder="What you asked for (optional)" value={f.text || ''} onChange={e => set('text', e.target.value)} style={{ flexBasis: '100%' }} />
              <button className="btn btn-secondary" disabled={busy} onClick={send}>{busy ? 'Recording...' : 'Record my request'}</button>
            </div>
          )}
          {msg ? <div style={{ fontSize: 12, marginTop: 4, color: msg.ok ? C.ok : C.bad }}>{msg.text}</div> : null}
        </div>
      ) : null}
      {loadErr ? <div style={{ color: C.bad, fontSize: 12 }}>The requests could not be loaded ({loadErr}). This is a load failure, not "none".</div>
        : rows.length === 0 ? <div style={{ color: C.dim, fontSize: 12 }}>No extension request is recorded on this claim.</div>
        : rows.map(r => (
          <div key={r.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px dotted ' + C.line, color: C.text }}>
            <b>{r.timeLimit}</b> - sent {r.sentOn} to {r.sentTo} by {MEANS_LABELS[r.means] || r.means}; last day {r.expiresOn}
            {' '}<span style={{ color: r.timely ? C.ok : C.bad }}>{r.timely ? 'on time' : 'after the last day'}</span>
            <div style={{ fontSize: 11, color: C.dim }}>Proof: {r.proof}{r.text ? ' - ' + r.text : ''} - by {r.byName || 'the claimant'}</div>
          </div>
        ))}
    </div>
  );
}
